
import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { 
  fetchMacroCurriculum, 
  fetchMesoCurriculum,
  MacroCurriculum,
  MesoCurriculum
} from '@/services/roadmap/roadmapService';
import { useToast } from '@/hooks/use-toast';
import { CurriculumTable } from './CurriculumTable';

type CurriculumTabProps = {
  type: 'macro' | 'meso';
  onEdit: (item: MacroCurriculum | MesoCurriculum, type: 'macro' | 'meso') => void;
  onCreate: (type: 'macro' | 'meso') => void;
  onDeleteConfirm: (id: string, type: 'macro' | 'meso') => void;
};

export interface CurriculumTabRef {
  refresh: () => Promise<void>;
}

export const CurriculumTab = forwardRef<CurriculumTabRef, CurriculumTabProps>(
  ({ type, onEdit, onCreate, onDeleteConfirm }, ref) => {
  const [data, setData] = useState<(MacroCurriculum | MesoCurriculum)[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const fetchFunction = type === 'macro' ? fetchMacroCurriculum : fetchMesoCurriculum;
      const fetchedData = await fetchFunction();
      setData(fetchedData);
    } catch (error) {
      console.error(`Error loading ${type} curriculum:`, error);
      toast({
        title: 'Error',
        description: `Failed to load ${type} curriculum data`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
    
  }, [type, toast]);

  useImperativeHandle(ref, () => ({
    refresh: loadData
  }), [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          {type === 'macro' ? 'Macro' : 'Meso'} Curriculum
        </h2>
        <Button variant="default" onClick={() => onCreate(type)}>
          Create New Entry
        </Button>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : data.length === 0 ? (
        <div className="border rounded-md p-6 bg-background">
          <p className="text-center text-muted-foreground">
            No curriculum entries yet. Click "Create New Entry" to get started.
          </p>
        </div>
      ) : (
        <CurriculumTable 
          data={data} 
          type={type} 
          onEdit={onEdit} 
          onDelete={(id) => onDeleteConfirm(id, type)}
        />
      )}
    </div>
  );
});
